// E8EquilateralTrianglePFClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 10) – P/F dla trojkata rownobocznego o boku a.
// Wysokosc h = (a/2)*sqrt(3), Pole = (a^2/4)*sqrt(3).

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import TruthInput from "../../../../components/inputs/TruthInput";

const A_VALUES = [2, 4, 6];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// latex dla k*sqrt(3) (k>=1 calkowite)
function rootCoef(k) {
  return k === 1 ? "\\sqrt{3}" : `${k}\\sqrt{3}`;
}

function buildData({ a, s1True, s2True }) {
  const ch = a / 2; // wspolczynnik wysokosci
  const ca = (a * a) / 4; // wspolczynnik pola
  const heightCorrect = rootCoef(ch);
  const areaCorrect = rootCoef(ca);

  // bledne (dystraktorowe) wartosci
  const heightWrong = rootCoef(a); // czesty blad: a*sqrt(3) zamiast (a/2)*sqrt(3)
  const areaWrong = rootCoef(ch); // blad: wartosc wysokosci jako pole

  const heightClaim = s1True ? heightCorrect : heightWrong;
  const areaClaim = s2True ? areaCorrect : areaWrong;

  return {
    a,
    heightClaim,
    areaClaim,
    correctPF: [s1True ? "P" : "F", s2True ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8EquilateralTrianglePFClosedTask",
    data: buildData({ a: 2, s1True: true, s2True: true }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 10,
    },
    tags: ["e8", "planimetria", "trojkat-rownoboczny", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8EquilateralTrianglePFClosedTask",
    data: buildData({
      a: pick(A_VALUES),
      s1True: Math.random() < 0.5,
      s2True: Math.random() < 0.5,
    }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_10" },
    tags: ["e8", "planimetria", "trojkat-rownoboczny", "prawda_falsz"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Dany jest trójkąt równoboczny o boku długości{" "}
        <InlineMath math={`${data.a}\\ \\text{cm}`} />.
      </p>
      <p>
        Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli zdanie
        jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>
      <p>
        1. Wysokość tego trójkąta jest równa{" "}
        <InlineMath math={`${data.heightClaim}\\ \\text{cm}`} />.
      </p>
      <p>
        2. Pole tego trójkąta jest równe{" "}
        <InlineMath math={`${data.areaClaim}\\ \\text{cm}^2`} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) => onChange?.({ ...value, [key]: nextValue });
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput
            value={value[key] || null}
            disabled={disabled}
            onChange={(nextValue) => setValue(key, nextValue)}
          />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    userAnswer?.s1 === data.correctPF[0] && userAnswer?.s2 === data.correctPF[1]
  );
}

export function renderAnswer({ data }) {
  return (
    <InlineMath
      math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath
      math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\quad 2.\\ \\text{${userAnswer?.s2 || "-"}}`}
    />
  );
}

export function renderExplanation({ data }) {
  const a = data.a;
  const ch = a / 2;
  const ca = (a * a) / 4;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Wysokość trójkąta równobocznego o boku{" "}
        <InlineMath math="a" /> wyraża się wzorem{" "}
        <InlineMath math="h=\frac{a\sqrt{3}}{2}" />.
      </p>
      <BlockMath math={`h=\\frac{${a}\\sqrt{3}}{2}=${rootCoef(ch)}\\ \\text{cm}`} />
      <p>
        <strong>Krok 2.</strong> Pole trójkąta równobocznego:{" "}
        <InlineMath math="P=\frac{a^2\sqrt{3}}{4}" />.
      </p>
      <BlockMath math={`P=\\frac{${a}^2\\sqrt{3}}{4}=${rootCoef(ca)}\\ \\text{cm}^2`} />
      <p>
        Stąd odpowiedzi:{" "}
        <InlineMath
          math={`1.\\ \\text{${data.correctPF[0]}},\\ 2.\\ \\text{${data.correctPF[1]}}`}
        />
        .
      </p>
    </div>
  );
}
